import {
  CanadianLicenseClass,
  Endorsement,
  LicenseClass,
  LicenseClassLabels,
  MexicanLicenseClass,
  MexicanLicenseClassLabels,
  CanadianLicenseClassLabels,
} from 'src/graphql/types/licenses'
import { findLicenseRegion } from 'src/lib/usStates'

const COMMERCIAL_CANADIAN_CLASSES: string[] = [
  CanadianLicenseClass.CLASS_1,
  CanadianLicenseClass.CLASS_2,
  CanadianLicenseClass.CLASS_3,
  CanadianLicenseClass.CLASS_4,
]

export const isMedicalExamExpirationRequired = (
  licenseClass: LicenseClass | string | null | undefined,
  country?: string | null
): boolean => {
  if (!licenseClass || licenseClass === LicenseClass.NO_CDL) return false

  // Canadian Class 5/6 are non-commercial (passenger / motorcycle).
  if (country === 'CA') {
    return COMMERCIAL_CANADIAN_CLASSES.includes(licenseClass)
  }

  // Class D is exempt only for US licenses (ENG-4363): MexicanLicenseClass.D
  // shares the same 'D' enum value but is a commercial freight class, and MX
  // applicants were required to provide the medical exam before the US
  // exemption existed. No country (legacy callers/data) keeps the US default.
  if (licenseClass === LicenseClass.D && country !== 'MX') return false

  return true
}

export const getLicenseClassOptions = (
  country?: string | null
): { value: string; label: string }[] => {
  if (country === 'MX') {
    return Object.values(MexicanLicenseClass).map((value) => ({
      value,
      label: MexicanLicenseClassLabels[value],
    }))
  }
  if (country === 'CA') {
    return Object.values(CanadianLicenseClass).map((value) => ({
      value,
      label: CanadianLicenseClassLabels[value],
    }))
  }
  return Object.values(LicenseClass).map((value) => ({
    value,
    label: LicenseClassLabels[value],
  }))
}

export const getValidLicenseClassValues = (country?: string | null): string[] =>
  getLicenseClassOptions(country).map((option) => option.value)

// OCR / free-text class onto a dropdown value for the license country.
export const coerceLicenseClassForCountry = (
  value: string | undefined,
  country?: string | null
): string | undefined => {
  if (!value) return undefined
  const valid = getValidLicenseClassValues(country)
  if (valid.includes(value)) return value
  if (country === 'CA') {
    const match = value.match(/([1-6])/)
    if (!match) return undefined
    const coerced = `CLASS_${match[1]}`
    return valid.includes(coerced) ? coerced : undefined
  }
  return undefined
}

export const resolveScannedLicensePrefill = (
  prefill: { state?: string; licenseClass?: string },
  currentCountry?: string | null
): {
  state?: string
  country?: string
  licenseClass?: string
} => {
  const region = findLicenseRegion(prefill.state)
  const country = region?.country ?? currentCountry ?? undefined
  const resolved: {
    state?: string
    country?: string
    licenseClass?: string
  } = { country }

  if (prefill.state !== undefined) {
    resolved.state = region?.value ?? prefill.state
  }

  if (prefill.licenseClass !== undefined) {
    const coerced = coerceLicenseClassForCountry(prefill.licenseClass, country)
    if (coerced) resolved.licenseClass = coerced
  }

  return resolved
}

export const getEndorsementsByLicenseClass = (
  licenseClass: LicenseClass | string | null | undefined
): Endorsement[] => {
  switch (licenseClass) {
    case LicenseClass.A:
      return [
        Endorsement.H,
        Endorsement.M,
        Endorsement.N,
        Endorsement.X,
        Endorsement.P,
        Endorsement.S,
        Endorsement.T,
      ]
    case LicenseClass.B:
      return [Endorsement.H, Endorsement.N, Endorsement.P, Endorsement.S]
    case LicenseClass.C:
      return [Endorsement.H, Endorsement.P, Endorsement.S]
    case LicenseClass.D:
      return [Endorsement.S, Endorsement.H, Endorsement.T, Endorsement.N]
    case LicenseClass.E:
      return [Endorsement.S, Endorsement.H, Endorsement.N]
    default:
      return [
        Endorsement.H,
        Endorsement.M,
        Endorsement.N,
        Endorsement.X,
        Endorsement.P,
        Endorsement.S,
        Endorsement.T,
      ]
  }
}
